import React, { useState, useEffect, useCallback } from 'react';
import { FileBarChart, Plus, Pencil, Trash2, Search, AlertCircle, Loader2, Globe, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { SlidePanel } from '@/components/ui/slide-panel';
import {
  knowledgeContentService,
  type KnowledgeEntry,
} from '@/services/knowledgeContentService';

interface ReportRecipesManagerProps {
  onUpdate?: () => void;
}

/**
 * AIC-1.2 — Admin manager for Report Recipes (step-by-step "how to build a
 * report on X" entries the assistant retrieves). Text entries (title + body),
 * no file upload, no embeddings. Global entries (is_global) are visible to all
 * tenants; non-global entries are scoped to the authoring tenant.
 */
export const ReportRecipesManager: React.FC<ReportRecipesManagerProps> = ({ onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isGlobal, setIsGlobal] = useState(true);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<KnowledgeEntry | null>(null);

  const loadEntries = useCallback(async (tid: string | null) => {
    setLoading(true);
    try {
      const list = await knowledgeContentService.listEntries('report_recipe', {
        tenantId: tid ?? undefined,
        includeGlobal: true,
      });
      setEntries(list);
    } catch (err) {
      console.error('Error loading report recipes:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      const { data: tid } = await supabase.rpc('get_current_user_tenant_id');
      if (!mounted) return;
      setTenantId(tid ?? null);
      await loadEntries(tid ?? null);
    };
    init();
    return () => {
      mounted = false;
    };
  }, [user, loadEntries]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setTitle('');
    setBody('');
    setIsGlobal(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setTitle('');
    setBody('');
    setIsGlobal(true);
    setShowForm(true);
  };

  const openEdit = (entry: KnowledgeEntry) => {
    setEditingId(entry.id);
    setTitle(entry.title);
    setBody(entry.body);
    setIsGlobal(entry.is_global);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!title.trim() || !body.trim()) {
      toast({
        title: 'Missing fields',
        description: 'A recipe needs both a title and step-by-step body.',
        variant: 'destructive',
      });
      return;
    }
    if (!user?.id || !tenantId) {
      toast({ title: 'Error', description: 'Could not resolve your tenant.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await knowledgeContentService.updateEntry(editingId, { title, body, is_global: isGlobal });
        toast({ title: 'Recipe updated', description: title });
      } else {
        await knowledgeContentService.createEntry({
          title,
          body,
          sourceType: 'report_recipe',
          isGlobal,
          tenantId,
          userId: user.id,
        });
        toast({ title: 'Recipe created', description: title });
      }
      resetForm();
      await loadEntries(tenantId);
      onUpdate?.();
    } catch (err) {
      console.error('Error saving recipe:', err);
      toast({ title: 'Error', description: 'Failed to save the recipe.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await knowledgeContentService.deleteEntry(deleteTarget.id);
    if (ok) {
      toast({ title: 'Recipe deleted', description: deleteTarget.title });
      await loadEntries(tenantId);
      onUpdate?.();
    } else {
      toast({ title: 'Error', description: 'Failed to delete the recipe.', variant: 'destructive' });
    }
    setDeleteTarget(null);
  };

  const filtered = entries.filter(
    (e) =>
      e.title.toLowerCase().includes(search.toLowerCase()) ||
      e.body.toLowerCase().includes(search.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileBarChart className="h-5 w-5" />
              Report Recipes
            </span>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              New Recipe
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Report-building guidance</h3>
            <p className="text-blue-700 text-sm">
              Write step-by-step recipes for "how do I build a report on X" questions. The assistant
              retrieves these to walk users through the dashboard / widget builder. Keep each recipe
              focused. Global recipes are shared with every tenant; non-global recipes stay private to
              this tenant.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search recipes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* List */}
          {filtered.length > 0 ? (
            <div className="space-y-2">
              <h3 className="font-medium text-sm text-muted-foreground">
                {filtered.length} recipe{filtered.length !== 1 ? 's' : ''}
              </h3>
              {filtered.map((entry) => (
                <Card key={entry.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <FileBarChart className="h-4 w-4 text-muted-foreground shrink-0" />
                        <h4 className="font-medium truncate">{entry.title}</h4>
                        {entry.is_global ? (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <Globe className="h-3 w-3" /> Global
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Building2 className="h-3 w-3" /> Tenant
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
                        {entry.body}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget(entry)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="font-medium mb-2">No recipes yet</h3>
              <p className="text-sm text-muted-foreground">
                {search ? 'No recipes match your search' : 'Create your first report recipe to get started'}
              </p>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit panel */}
      <SlidePanel
        isOpen={showForm}
        onClose={resetForm}
        title={editingId ? 'Edit Recipe' : 'New Recipe'}
        width="lg"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recipe-title">Title</Label>
            <Input
              id="recipe-title"
              placeholder="e.g. Report on installations by state"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="recipe-body">Steps</Label>
            <Textarea
              id="recipe-body"
              placeholder={'1. Open the dashboard\n2. Add a widget\n3. Set the data source to Projects\n4. Group by Project State'}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="recipe-global" className="font-medium">Global recipe</Label>
              <p className="text-xs text-muted-foreground">Share with all tenants (off = this tenant only)</p>
            </div>
            <Switch id="recipe-global" checked={isGlobal} onCheckedChange={setIsGlobal} />
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button variant="outline" onClick={resetForm} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingId ? 'Save changes' : 'Create recipe'
              )}
            </Button>
          </div>
        </div>
      </SlidePanel>

      {/* Delete confirmation */}
      <SlidePanel
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Recipe"
        width="md"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmDelete} variant="destructive">
              Delete
            </Button>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
};
